import { User } from '../../models/User'
import React, { useCallback, useEffect } from 'react'
import { Modal } from '../../components/Modal/Modal'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useDeleteUserByIdMutation } from '../../store/user/user'
import { getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils'

interface DeleteUserModalProps {
  user?: User
  show: boolean
  onHide: () => void
  onUserDeleted: () => void
}

export const DeleteUserModal: React.FC<DeleteUserModalProps> = ({ user, show, onHide, onUserDeleted }) => {
  const accountId = useAppSelector(selectAccountId)
  const [deleteUser, deleteUserResult] = useDeleteUserByIdMutation()

  const handleDeleteUser = useCallback(() => {
    if (user) {
      deleteUser({ accountId, userId: user.id })
    }
  }, [accountId, deleteUser, user])

  useEffect(() => {
    if (deleteUserResult.isSuccess) {
      onUserDeleted()
    }
  }, [deleteUserResult.isSuccess, onUserDeleted])

  if (!user) {
    return null
  }

  const displayName = getUserDisplayName(user)

  const body = (
    <div className='d-flex flex-column gap-4'>
      <div className='d-flex flex-column gap-3'>
        <p className='paragraph-medium mb-0'>
          By deleting <strong>{displayName}</strong>, their account <strong>will be permanently disabled </strong>
          and all their data will be removed. Deleting <strong>cannot be undone</strong>.
        </p>
        <div>
          <p className='paragraph-medium fw-bold mb-0'>What will be lost:</p>
          <ul className='paragraph-medium mb-0 ps-4'>
            <li>All associated meetings and notes</li>
            <li>All associated contacts and client information</li>
            <li>Some statistical data (this may affect team reports)</li>
          </ul>
        </div>
      </div>
      {user.status !== 'deactivated' && (
        <div className='d-flex gap-3 align-items-center p-3 rounded-2 zocks-bg-gray-100 border'>
          <i className='bi bi-info-circle-fill zocks-text-gray-700' />
          <p className='paragraph-small mb-0 zocks-text-gray-700'>
            <strong>Deactivate</strong> instead of deleting if you want <strong>to keep past meetings and contacts</strong> owned by {displayName} accessible.
          </p>
        </div>
      )}
    </div>
  )

  return (
    <Modal
      centered={true}
      show={show}
      onHide={onHide}
      header={<h4>Delete User</h4>}
      headerBodySeparator
      body={body}
      showFooterActions={true}
      onFooterPrimaryAction={handleDeleteUser}
      footerPrimaryActionLoading={deleteUserResult.isLoading}
      footerPrimaryActionTitle='Delete'
      footerPrimaryActionButtonVariant='danger'
      footerSecondaryActionDisabled={deleteUserResult.isLoading}
    />
  )
}
